import { useState, createContext, useContext } from "react";
import { cn } from "@/utils/utils";
import { Toggle, type ToggleProps } from "./toggle";
import React from "react";

type FilterContextType = {
  activeOptions: string[];
  toggleOption: (x: string) => void;
};

const FilterContext = createContext<FilterContextType | null>(null);

const useFilter = () => {
  const context = useContext(FilterContext);
  if (!context) throw new Error("useFilter must be used within a Filter");
  return context;
};

export interface FilterProps 
  extends React.HTMLAttributes<HTMLDivElement>
  {
    onFilterChange?: (active: string[]) => void;
    multiSelect?: boolean;
    defaultActive?: string[];
    // this makes it a controlled prop - not sure how this'll go
    activeOptions?: string[]; 
  };

export const Filter = React.forwardRef<HTMLDivElement, FilterProps>(({
    onFilterChange,
    multiSelect = false,
    className,
    style,
    defaultActive = [],
    activeOptions: controlledActiveOptions, 
    children,
  },
  ref) => {
    
    // determine if component is controlled or uncontrolled
    const isControlled = controlledActiveOptions !== undefined;
    
    if (defaultActive.length > 1 && !multiSelect) {
      throw new Error("Too many tabs selected for single select filter");
    }

    // a little complicated
    // only use local state if not controlled
    const [internalActiveOptions, setInternalActiveOptions] = useState<string[]>(defaultActive);
    
    // use controlled state if provided, otherwise use internal state
    const activeOptions = isControlled ? controlledActiveOptions : internalActiveOptions;

    const toggleOption = React.useCallback(
      (x: string) => {
        const nextOptions = multiSelect
          ? (activeOptions.includes(x) ? activeOptions.filter(o => o !== x) : [...activeOptions, x])
          : [x];
        
        if (!isControlled) {
          setInternalActiveOptions(nextOptions);
        }
        
        onFilterChange?.(nextOptions);
      },
      [multiSelect, onFilterChange, activeOptions, isControlled]
    );

    const value = React.useMemo(
      () => ({ activeOptions, toggleOption }),
      [activeOptions, toggleOption]
    );

    return (
      <FilterContext.Provider value={value}>
        <div ref={ref} className={cn("flex flex-col", className)} style={style}>
          {children}
        </div>
      </FilterContext.Provider>
    );
  }
);
Filter.displayName = "Filter";

export interface FilterHeaderProps 
  extends React.HTMLAttributes<HTMLHeadingElement> {}

export const FilterHeader = React.forwardRef<HTMLHeadingElement, FilterHeaderProps>(
  ({ className, style, children, ...props}, ref) => (
    <h2
      ref={ref}
      {...props}
      className={cn("text-xl font-bold text-musio-light-lilac", className)}
    >
     {children}
    </h2>
  )
);
FilterHeader.displayName = "FilterHeader";

export interface FilterSubheaderProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

export const FilterSubheader = React.forwardRef<HTMLParagraphElement, FilterSubheaderProps>(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      {...props}
      className={cn("text-musio-text-light", className)}
    >
      {children}
    </p>
  )
);
FilterSubheader.displayName = "FilterSubheader"

export interface FilterOptionProps 
  extends Omit<ToggleProps, "pressed" | "onPressedChange"> {
    children: React.ReactNode;
    onPressedChange?: (next: boolean) => void;
  }

export const FilterOption = React.forwardRef<
  React.ComponentRef<typeof Toggle>,
  FilterOptionProps
>((
  { children, className, style, onPressedChange, ...rest },
  ref
) => {
  const { activeOptions, toggleOption } = useFilter();

  const option = String(children);
  const isActive = React.useMemo(
    () => activeOptions.includes(option),
    [activeOptions, option]
  );

  const handlePressedChange = (next: boolean) => {
    toggleOption(option);
    onPressedChange?.(next);
  };

  return (
    <Toggle
      ref={ref}
      className={cn(
        "Toggle font-bold text-sm rounded-full data-[state=on]:text-musio-mid-gray",
        className
      )}
      style={style}
      aria-label={`Toggle ${option}`}
      pressed={isActive}
      onPressedChange={handlePressedChange}
      {...rest}
    >
      {children}
    </Toggle>
  );
});
FilterOption.displayName = "FilterOption";

export interface FilterSectionProps
  extends React.HTMLAttributes<HTMLDivElement> {}

export const FilterSection = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col items-center justify-center gap-2", className)} {...props}>
      {children}
    </div>
  )
);
FilterSection.displayName = "FilterSection";
